// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Force-shred confirmation dialog for I-9 documents (global admin only)
 *
 * Normal shredding is limited to terminal documents whose retention window has passed.
 * A global admin can override that and move a document to the Shredded bucket from any
 * processing bucket regardless of retention. Because that bypasses the usual guards,
 * this dialog requires the admin to type the document id to confirm — mirroring the
 * "type to confirm" pattern used for other destructive, irreversible-feeling actions.
 */
import { useEffect, useState } from 'react'
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
} from '@/components/ui'
import { Scissors } from 'lucide-react'

interface ShredDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  docId: string | null
  status: string | null
  isShredding: boolean
  onConfirm: () => void
}

export const ShredDialog: FC<ShredDialogProps> = ({
  open,
  onOpenChange,
  docId,
  status,
  isShredding,
  onConfirm,
}) => {
  const [typed, setTyped] = useState('')

  // Clear the confirmation field whenever the dialog reopens for a new document.
  useEffect(() => {
    if (open) setTyped('')
  }, [open, docId])

  const confirmed = docId !== null && typed.trim() === docId

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5 text-destructive" />
            Force Shred Document
          </DialogTitle>
          <DialogDescription>
            Move <span className="font-mono">{docId}</span> (status{' '}
            <span className="font-mono">{status}</span>) to the Shredded bucket. This
            bypasses the usual terminal-status and retention-window checks and should only
            be done after the paper document has been physically destroyed. To confirm,
            type the document id below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-4">
          <Label htmlFor="shredConfirmId">Document ID</Label>
          <Input
            id="shredConfirmId"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={docId ?? ''}
            className="font-mono"
            autoComplete="off"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isShredding || !confirmed}
          >
            <Scissors className="h-4 w-4 mr-1" />
            Shred
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
